/**
 * Schémas Zod d'authentification : inscription, connexion, rafraîchissement, déconnexion.
 * Référence : document 06 § 3.2.
 */
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH, MFA_CODE_LENGTH, MIN_PASSWORD_LENGTH, } from '../constants/limits.constants.js';
import { VAT_REGIMES } from '../enums/platform.enum.js';
import { toFirstNameCase, toLastNameCase } from '../utils/format.utils.js';
import { z } from 'zod';
/** Adresse e-mail normalisée (minuscules, espaces retirés). */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(MAX_EMAIL_LENGTH)
    .email({ message: 'Adresse e-mail invalide.' });
/**
 * Mot de passe : au moins 12 caractères, une minuscule, une majuscule,
 * un chiffre et un caractère spécial.
 */
export const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, {
    message: `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`,
})
    .max(MAX_PASSWORD_LENGTH)
    .regex(/[a-z]/, { message: 'Le mot de passe doit contenir au moins une minuscule.' })
    .regex(/[A-Z]/, { message: 'Le mot de passe doit contenir au moins une majuscule.' })
    .regex(/[0-9]/, { message: 'Le mot de passe doit contenir au moins un chiffre.' })
    .regex(/[^A-Za-z0-9]/, {
    message: 'Le mot de passe doit contenir au moins un caractère spécial.',
});
/** Identifiant lisible de société : minuscules, chiffres et tirets. */
export const tenantSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
    message: 'Identifiant invalide (minuscules, chiffres et tirets uniquement).',
});
/** Raison sociale. */
export const legalNameSchema = z.string().trim().min(2).max(180);
/** SIREN : 9 chiffres, clé de Luhn non exigée à ce niveau. */
export const sirenSchema = z
    .string()
    .trim()
    .regex(/^\d{9}$/, { message: 'Le SIREN doit comporter exactement 9 chiffres.' });
/** SIRET : 14 chiffres. */
export const siretSchema = z
    .string()
    .trim()
    .regex(/^\d{14}$/, { message: 'Le SIRET doit comporter exactement 14 chiffres.' });
/** Nom de famille : la casse saisie est normalisée en capitales. */
const optionalLastName = z.string().trim().min(1).max(80).transform(toLastNameCase).optional();
/** Prénom : la casse saisie est normalisée (capitale initiale, reste en minuscules). */
const optionalFirstName = z.string().trim().min(1).max(80).transform(toFirstNameCase).optional();
export const registerCompanySchema = z.object({
    legalName: legalNameSchema,
    slug: tenantSlugSchema,
    email: emailSchema,
    password: passwordSchema,
    firstName: optionalFirstName,
    lastName: optionalLastName,
    vatRegime: z.enum(VAT_REGIMES).default('normal'),
    siren: sirenSchema.optional(),
    siret: siretSchema.optional(),
});
/** Code à six chiffres produit par l'application d'authentification. */
export const mfaCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, { message: `Le code MFA doit comporter ${MFA_CODE_LENGTH} chiffres.` });
/**
 * Second facteur accepté à la connexion et à la désactivation : code TOTP à six chiffres
 * ou code de secours (dix caractères alphanumériques, tiret facultatif).
 * La valeur est normalisée (majuscules, séparateurs retirés) avant vérification.
 */
export const mfaSecondFactorSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())
    .refine((value) => /^\d{6}$/.test(value) || /^[A-Z0-9]{10}$/.test(value), {
    message: 'Le code doit comporter 6 chiffres ou 10 caractères alphanumériques.',
});
/** Mot de passe exigé pour les opérations sensibles sur le second facteur. */
export const currentPasswordSchema = z.string().min(1).max(MAX_PASSWORD_LENGTH);
/** Jeton d'invitation opaque (base64url), transmis par le lien public. */
export const invitationTokenSchema = z.string().trim().min(32).max(512);
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(MAX_PASSWORD_LENGTH),
    tenantSlug: tenantSlugSchema.optional(),
    invitationToken: invitationTokenSchema.optional(),
    mfaCode: mfaSecondFactorSchema.optional(),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(32).max(512),
});
export const logoutSchema = z.object({
    refreshToken: z.string().min(32).max(512).optional(),
    allDevices: z.boolean().default(false),
});
/** Prévisualisation publique d'une invitation. */
export const invitationPreviewSchema = z.object({
    token: invitationTokenSchema,
});
/** Création d'un compte depuis une invitation. */
export const registerInvitationSchema = z.object({
    token: invitationTokenSchema,
    password: passwordSchema,
    firstName: optionalFirstName,
    lastName: optionalLastName,
});
/** `POST /auth/mfa/verify` : confirmation de l'enrôlement TOTP. */
export const mfaVerifySchema = z.object({
    code: mfaCodeSchema,
});
/** `POST /auth/mfa/disable` : désactivation du second facteur. */
export const mfaDisableSchema = z.object({
    password: currentPasswordSchema,
    code: mfaSecondFactorSchema,
});
/** `POST /auth/mfa/backup-codes` : régénération des codes de secours. */
export const mfaBackupCodesSchema = z.object({
    password: currentPasswordSchema,
});
//# sourceMappingURL=auth.schema.js.map